import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, PageHeader, Select, Space, Switch, Table, Tag, Tooltip, message } from 'antd';
import _ from 'lodash';
import {
  OperationWallet,
  OperationWalletProvider,
  PayoutCursorStatus,
} from '../../domain/usdt-providers/operationWalletProvider';
import {
  CCursorStatus,
  CReceiverProvider,
} from '../../domain/usdt-providers/cReceiverProvider';
import { Environment } from '../../core/configs/services';
import InternalTransferModal from '../components/InternalTransferModal';
import RebirthModal from '../components/RebirthModal';
import _module from '../configs/module';
import { WRITE_SYSTEM_SETTING } from '../configs/scopes';
import { normalizeOtp } from '../../core/utils/otp';

/** 面板餘額是 10 分鐘的快取,鏈上才是真的。超過這個歲數就標灰,提醒別盡信。 */
const STALE_MIN = 15;

function tronscanAddressUrl(address: string): string {
  const env = Environment() ?? 'develop';
  const base = _.find(['prod', 'stg'], (x) => x === env)
    ? 'https://tronscan.org/#/address/'
    : 'https://shasta.tronscan.org/#/address/';
  return base + address;
}

const num = (v?: string | number) =>
  v != null && v !== ''
    ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : '-';

/**
 * 列表排序權重:RENTAL(能量站,全系統唯一一顆)永遠第一列,再來是開啟中的出款錢包,
 * 停用的沉到最後。同一組內仍照 id 新到舊。
 *
 * 為什麼要特別處理 RENTAL:它 id=1,單純按 id 降冪會被上百顆出款錢包擠到最後一頁,
 * 但它是所有委派能量的來源,要能一眼看到餘額。
 */
const walletRank = (r: OperationWallet) =>
  r.type === 'RENTAL' ? 0 : r.enabled === 1 ? 1 : 2;

const OperationWalletManager = (props: { title: string }) => {
  const [rows, setRows] = useState<OperationWallet[]>([]);
  const [cursor, setCursor] = useState<PayoutCursorStatus | null>(null);
  const [cCursor, setCCursor] = useState<CCursorStatus | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [addVisible, setAddVisible] = useState(false);
  const [transferSource, setTransferSource] = useState<OperationWallet | null>(null);
  const [rebirthSource, setRebirthSource] = useState<OperationWallet | null>(null);
  const [form] = Form.useForm();
  const canWrite = _module.can([WRITE_SYSTEM_SETTING]);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await OperationWalletProvider.list('TRC');
      setRows(r.data || []);
      // 游標是附加資訊,拿不到就顯示「—」,不能讓它擋住錢包列表。
      OperationWalletProvider.payoutCursor('TRC').then(setCursor).catch(() => setCursor(null));
      CReceiverProvider.cursorStatus().then(setCCursor).catch(() => setCCursor(null));
    } catch (e: any) {
      message.error(e?.message || 'load failed');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 強制查鏈更新餘額,再重載。
   *
   * 列表上的餘額是 operation_wallet 的快取(每分鐘一次 cron)。要在轉帳前後看真實數字,
   * 就得逼它現在去查鏈 —— 這是「面板顯示 388k、鏈上只有 137k」那件事之後補的。
   */
  const refreshBalances = async () => {
    setLoading(true);
    try {
      await OperationWalletProvider.refreshBalances();
      const r = await OperationWalletProvider.list('TRC');
      setRows(r.data || []);
      OperationWalletProvider.payoutCursor('TRC').then(setCursor).catch(() => setCursor(null));
      CReceiverProvider.cursorStatus().then(setCCursor).catch(() => setCCursor(null));
      message.success('餘額已更新');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || '更新失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, []);

  const onAdd = async () => {
    const v = await form.validateFields();
    try {
      await OperationWalletProvider.create({ channel: 'TRC', ...v });
      message.success('已新增');
      setAddVisible(false);
      form.resetFields();
      reload();
    } catch (e: any) {
      message.error(e?.response?.data?.displayMessage || e?.message || '新增失敗');
    }
  };

  // 啟用中的出款錢包不能內轉 —— 內轉會跟在途出款搶同一顆錢包的能量/鎖,清空一顆
  // 還在收單的錢包很容易失敗。逼人先停用(停用也保證沒有訂單在途)。後端也會再擋一次。
  const onInternalTransfer = (row: OperationWallet) => {
    if (row.type === 'PAYOUT' && row.enabled === 1) {
      message.warning('啟用中的出款錢包不能內轉 —— 請先在列表把它停用,再執行內轉');
      return;
    }
    setTransferSource(row);
  };

  // 重生 = 建新錢包 → 註冊成停用的出款錢包 → 把舊的清空進去。第三步就是內轉,所以同一個
  // 規則:啟用中的錢包擋下。先停用才動它,也保證沒有訂單在途。
  const onRebirth = (row: OperationWallet) => {
    if (row.enabled === 1) {
      message.warning('啟用中的出款錢包不能重生 —— 請先在列表把它停用');
      return;
    }
    setRebirthSource(row);
  };

  const onToggle = async (row: OperationWallet, enabled: boolean) => {
    if (!enabled && row.isDefault === 1) {
      message.warning('這是目前預設,請先指定另一顆為預設再停用');
      return;
    }
    try {
      await OperationWalletProvider.update(row.id, { enabled: enabled ? 1 : 0 });
      reload();
    } catch (e: any) {
      message.error(e?.response?.data?.displayMessage || e?.message);
    }
  };

  // 可歸集狀態:獨立於出款(enabled)—— 有開才收歸集來的帳。
  const onToggleCollectible = async (row: OperationWallet, collectible: boolean) => {
    try {
      await OperationWalletProvider.update(row.id, { collectible: collectible ? 1 : 0 });
      message.success(collectible ? '已開啟可歸集' : '已關閉可歸集');
      reload();
    } catch (e: any) {
      message.error(e?.response?.data?.displayMessage || e?.message);
    }
  };

  const onSetDefault = (row: OperationWallet) => {
    const cur = rows.find((r) => r.type === row.type && r.isDefault === 1);
    Modal.confirm({
      title: '設為預設',
      content:
        cur && cur.id !== row.id
          ? `此類型(${row.type})目前預設 ${cur.address} 將被取消,改為 ${row.address},確定?`
          : `將 ${row.address} 設為 ${row.type} 預設?`,
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await OperationWalletProvider.setDefault(row.id);
          message.success(
            `已設預設${res.data?.replacedAddress ? `,原預設 ${res.data.replacedAddress} 已取消` : ''}`,
          );
          reload();
        } catch (e: any) {
          message.error(e?.response?.data?.displayMessage || e?.message);
        }
      },
    });
  };

  // 硬刪除已於 2026-08-18 移除 —— 它只擋「啟用中的預設」,25 顆運營錢包裡只護到 1 顆,
  // 其餘 6 顆正在跑、身上共 23 萬 USDT 的出款錢包,一個確認框就從註冊表消失。
  // 改成棄用:紀錄留著(只是停用 + 不可歸集),並進到棄用錢包頁繼續追殘值。
  const onAbandon = (row: OperationWallet) => {
    if (row.isDefault === 1) {
      message.warning('目前的預設錢包不可棄用,請先指定另一顆為預設');
      return;
    }
    Modal.confirm({
      title: '棄用此運營錢包',
      width: 520,
      centered: true,
      content: (
        <div>
          <p>
            {row.type} 錢包 <strong>{row.address}</strong> 將被棄用。
          </p>
          <p>棄用後:停用出款、不再收歸集,並列入棄用錢包頁繼續追鏈上殘值。紀錄不會被刪除。</p>
          <p style={{ color: '#a8071a' }}>
            鏈上 USDT 與 TRX 都必須是 0 才能棄用。還有餘額請先用「內轉」移到其他運營錢包。
          </p>
        </div>
      ),
      okText: '確認棄用',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        // 攔截器已經對失敗的請求跳過訊息了,這裡不再重複跳(會變成兩個一樣的紅框)。
        await OperationWalletProvider.abandon(row.id);
        reload();
      },
    });
  };

  /**
   * 切換 RENTAL 來源。
   *
   * 啟用中的出款錢包不擋 —— 應急切換就是要在跑的時候改;在途那筆已經拿到能量了,
   * 下一筆才用新來源。
   *
   * 框裡的警語刻意不查鏈:查鏈要多一個端點,而節點慢的時候會讓確認框卡住,
   * 這些資訊本來就是固定的。
   */
  const openRentalSourceModal = (row: OperationWallet) => {
    let picked: string | null = row.rentalAddress ?? null;
    let code = '';
    const current = row.rentalAddress
      ? row.rentalAddress
      : `${rentalOptions.find((r) => r.isDefault === 1)?.address ?? '(未設定預設)'}(預設)`;

    Modal.confirm({
      title: '切換 RENTAL 來源',
      width: 600,
      centered: true,
      content: (
        <div>
          <p style={{ marginBottom: 4 }}>
            出款錢包 <strong>{row.address}</strong>
          </p>
          <p style={{ marginBottom: 12, color: '#666' }}>目前:{current}</p>

          <p style={{ marginBottom: 4 }}>改為:</p>
          <Select
            style={{ width: '100%', marginBottom: 12 }}
            defaultValue={picked ?? ''}
            onChange={(v) => (picked = v === '' ? null : v)}
            options={[
              { value: '', label: '(預設 RENTAL)' },
              ...rentalOptions.map((r) => ({
                value: r.address,
                label: r.isDefault === 1 ? `${r.address}(目前的預設)` : r.address,
              })),
            ]}
          />

          <p style={{ marginBottom: 4 }}>這顆錢包接下來這四件事都改由新來源出:</p>
          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
            <li>出款能量委派</li>
            <li>借帶寬</li>
            <li>自動補 TRX</li>
            <li>重生時開通新錢包</li>
          </ul>

          <p style={{ color: '#a8071a', marginBottom: 8 }}>
            ⚠ 舊來源已經委派出去的能量/帶寬不會立刻收回,由每分鐘的回收工作在錢包閒置時
            自然收回。未收完前錢包身上有委派足跡,C 池供應商可能拒絕供能。
          </p>
          <p style={{ color: '#a8071a', marginBottom: 12 }}>
            ⚠ 新來源必須已在鏈上質押。能量不足或被停用時會自動退回預設 RENTAL 並發 TG 告警。
          </p>

          <p style={{ marginBottom: 4 }}>請輸入 2FA 驗證碼:</p>
          <Input placeholder="2FA 驗證碼" onChange={(e) => (code = e.target.value)} />
        </div>
      ),
      okText: '確認切換',
      cancelText: '取消',
      onOk: async () => {
        if (!code) {
          message.error('請輸入 2FA 驗證碼');
          throw new Error('no code');
        }
        try {
          await OperationWalletProvider.setRentalSource(row.id, picked, normalizeOtp(code));
          message.success('已切換 RENTAL 來源');
          reload();
        } catch (e: any) {
          // 攔截器已經對失敗的請求跳過訊息了,這裡只負責讓 Modal 留著讓人重試。
          throw e;
        }
      },
    });
  };

  // 可選的來源 = 啟用中的 RENTAL。從同一份列表資料算,不另打 API。
  const rentalOptions = rows.filter((r) => r.type === 'RENTAL' && r.enabled === 1);

  const columns = [
    {
      title: '地址',
      dataIndex: 'address',
      align: 'center' as const,
      render: (address: string) => (
        <>
          {/* eslint-disable-next-line react/jsx-no-target-blank */}
          <a href={tronscanAddressUrl(address)} target="_blank" rel="noreferrer">
            {address}
          </a>
          {cursor?.nextAddress === address && (
            <Tooltip title="不做金額過濾時,下一筆出款會輪到這顆">
              <Tag color="processing" style={{ marginLeft: 8 }}>
                下一筆
              </Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: '類型',
      dataIndex: 'type',
      align: 'center' as const,
      render: (t: string) => <Tag color={t === 'PAYOUT' ? 'green' : 'blue'}>{t}</Tag>,
    },
    {
      title: '餘額(USDT)',
      dataIndex: 'balance',
      align: 'right' as const,
      render: (v: string) => num(v),
    },
    {
      title: '餘額(TRX)',
      dataIndex: 'trxBalance',
      align: 'right' as const,
      render: (v: string) => num(v),
    },
    {
      title: '更新時間',
      dataIndex: 'balanceUpdatedAt',
      align: 'center' as const,
      render: (v: string) => {
        if (!v) return '-';
        const stale = Date.now() - new Date(v).getTime() > STALE_MIN * 60 * 1000;
        const text = new Date(v).toLocaleString();
        return stale ? (
          <Tooltip title={`餘額是快取,每 10 分鐘刷新一次。已超過 ${STALE_MIN} 分鐘沒更新，點地址可到 tronscan 核對`}>
            <span style={{ color: '#999' }}>{text}</span>
          </Tooltip>
        ) : (
          text
        );
      },
    },
    {
      title: '開立時間',
      dataIndex: 'createdAt',
      align: 'center' as const,
      // operation_wallet 的登記時間。「重生」是先鑄錢包、下一個呼叫就登記,兩者只差毫秒,
      // 所以這個欄位對出款錢包等同開立時間;只有 migration 進來的 TWgJyC 例外(2024 鑄、
      // 2026-07-08 才登記)。要真正的鏈上鑄造時間得後端 join vc_wallet_v2,不值得為一顆改。
      render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
    },
    {
      title: '出款狀態',
      dataIndex: 'enabled',
      align: 'center' as const,
      render: (v: number, row: OperationWallet) => (
        <Switch checked={v === 1} disabled={!canWrite} onChange={(c) => onToggle(row, c)} />
      ),
    },
    {
      title: '可歸集狀態',
      dataIndex: 'collectible',
      align: 'center' as const,
      render: (v: number, row: OperationWallet) =>
        row.type === 'PAYOUT' ? (
          <Switch
            checked={v === 1}
            disabled={!canWrite}
            onChange={(c) => onToggleCollectible(row, c)}
          />
        ) : null,
    },
    {
      title: '出款C游標',
      align: 'right' as const,
      // per-wallet:每顆出款錢包各輪各的 C 環(key 綁地址,存 poke-jobs Redis)。
      // 數字=累計 INCR 值;下一顆 C = 游標 % 環大小。拿不到清單顯示「—」。
      render: (_v: any, row: OperationWallet) => {
        if (row.type !== 'PAYOUT') return null;
        if (!cCursor) return '—';
        const w = cCursor.wallets.find((x) => x.address === row.address);
        if (!w) return (0).toLocaleString();
        // 只顯示數字的話推不出是哪顆 C:要自己知道環有幾顆再心算取模,而環會因為新增/停用
        // C 地址而變大變小,C 名單頁的「#」又只是當下排序的位次。所以連地址一起顯示。
        return (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <span>{w.cursor.toLocaleString()}</span>
            {w.nextAddress && (
              <Tooltip title={`下一筆這顆錢包會用 C 地址 ${w.nextAddress}(環第 ${(w.nextIndex ?? 0) + 1} 顆,共 ${cCursor.ringSize} 顆)`}>
                <span style={{ color: '#999', fontSize: 12 }}>
                  → {w.nextAddress.slice(0, 6)}…{w.nextAddress.slice(-4)}
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'RENTAL 來源',
      dataIndex: 'rentalAddress',
      align: 'center' as const,
      render: (v: string | null, row: OperationWallet) => {
        if (row.type !== 'PAYOUT') return '—';
        const label = v ? `${v.slice(0, 6)}…${v.slice(-4)}` : '預設';
        return (
          <Space direction="vertical" size={0}>
            <Button
              size="small"
              type={v ? 'default' : 'text'}
              disabled={!canWrite}
              onClick={() => openRentalSourceModal(row)}
            >
              {label}
            </Button>
            {row.rentalUpdatedAt && (
              <Tooltip title={`由 ${row.rentalUpdatedBy || '(未記錄)'} 修改`}>
                <span style={{ color: '#999', fontSize: 12 }}>
                  {new Date(row.rentalUpdatedAt).toLocaleString()}
                </span>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: '預設',
      dataIndex: 'isDefault',
      align: 'center' as const,
      render: (v: number, row: OperationWallet) =>
        v === 1 ? (
          <Tag color="gold">預設</Tag>
        ) : (
          <Button size="small" disabled={!canWrite} onClick={() => onSetDefault(row)}>
            設為預設
          </Button>
        ),
    },
    { title: '備註', dataIndex: 'note', align: 'left' as const },
    {
      title: '操作',
      align: 'center' as const,
      render: (_v: any, row: OperationWallet) => (
        <Space>
          <Button size="small" disabled={!canWrite} onClick={() => onInternalTransfer(row)}>
            內轉
          </Button>
          {row.type === 'PAYOUT' && (
            <Button size="small" disabled={!canWrite} onClick={() => onRebirth(row)}>
              重生
            </Button>
          )}
          <Button size="small" danger disabled={!canWrite} onClick={() => onAbandon(row)}>
            棄用
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={props.title}
        subTitle=""
        extra={
          canWrite
            ? [
                <Button key="refresh" onClick={refreshBalances} loading={loading}>
                  重新整理餘額
                </Button>,
                <Button key="add" type="primary" onClick={() => setAddVisible(true)}>
                  新增錢包
                </Button>,
              ]
            : []
        }
      />
      <Card>
        <Space style={{ marginBottom: 16 }} align="center" wrap>
          <Input.Search
            style={{ width: 420 }}
            placeholder="地址模糊搜尋"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Tooltip
            title={
              '出款錢包用 Redis 計數器輪替(payout:rr:TRC),每筆出款 +1 再對「啟用中的錢包數」取模。' +
              '「下一筆」是不做金額過濾時的預測 —— 實際出款會先剔除鏈上 USDT 不夠這筆金額的錢包,' +
              '候選一少,輪到的就可能不是這顆。跟 C 名單那個游標是兩回事,那個輪的是供能地址。'
            }
          >
            <span style={{ color: '#666' }}>
              出款輪詢游標:<b>{cursor && cursor.counter !== null ? cursor.counter : '—'}</b>
              {cursor && cursor.ringSize > 0 && (
                <span style={{ marginLeft: 8 }}>環 {cursor.ringSize} 顆</span>
              )}
              {cursor && cursor.nextIndex !== null && cursor.nextAddress && (
                <span style={{ marginLeft: 8 }}>
                  下一筆輪到 第 {cursor.nextIndex + 1} 顆 {cursor.nextAddress}
                </span>
              )}
            </span>
          </Tooltip>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows
            .filter((r) => !search.trim() || r.address?.toLowerCase().includes(search.trim().toLowerCase()))
            .sort((a, b) => walletRank(a) - walletRank(b) || Number(b.id) - Number(a.id))}
          columns={columns as any}
          // 預設 20:出款錢包十幾顆再加 RENTAL,一頁 10 會被切成兩頁,開關要翻頁很煩(2026-09-08)
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>
      <InternalTransferModal
        open={!!transferSource}
        source={transferSource}
        wallets={rows}
        onClose={() => setTransferSource(null)}
        onDone={() => {
          setTransferSource(null);
          reload();
        }}
      />
      <RebirthModal
        open={!!rebirthSource}
        source={rebirthSource}
        onClose={() => setRebirthSource(null)}
        onDone={reload}
      />
      <Modal
        title="新增運營錢包"
        visible={addVisible}
        onOk={onAdd}
        onCancel={() => setAddVisible(false)}
        okText="新增"
        cancelText="取消"
      >
        <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <Form.Item name="address" label="錢包地址" rules={[{ required: true }]}>
            <Input placeholder="須已存在於 vc_wallet" />
          </Form.Item>
          <Form.Item name="type" label="類型" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'RENTAL', label: 'RENTAL(租用能量)' },
                { value: 'PAYOUT', label: 'PAYOUT(出款)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="note" label="備註">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default OperationWalletManager;
